import React from 'react';
import { Link } from 'react-router-dom';

const Impact = () => (
  <div className="bg-ehh-cream text-ehh-navy">
    {/* Hero Section - Inspiring impact-focused image */}
    <section className="relative min-h-[80vh] flex items-center justify-center text-center overflow-hidden">
      <img 
        src="/assets/images/waving-children.jpg" 
        alt="Joyful Zambian children waving, symbolising hope and transformed futures" 
        className="absolute inset-0 w-full h-full object-cover" 
      />
      <div className="absolute inset-0 bg-ehh-navy/75"></div>
      <div className="relative z-10 container mx-auto px-6 sm:px-8 md:px-12 max-w-5xl">
        <div className="py-12 sm:py-16 md:py-20">
          <img src="/logo.png" alt="EMA House of Hope Logo" className="mx-auto h-24 sm:h-28 md:h-32 mb-6 sm:mb-8" />
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 sm:mb-6 text-ehh-cream">
            Measuring Our Impact
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl max-w-4xl mx-auto mb-8 sm:mb-10 leading-relaxed text-ehh-peach px-4 sm:px-0">
            Through synchronised interventions, EMA House of Hope creates measurable, sustainable change—stabilising lives, building skills, and strengthening health systems for resilient communities in Zambia.
          </p>
        </div>
      </div>
    </section>

    {/* Stories of Transformation - Generalised for consistency */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Stories of Transformation</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="bg-white rounded-2xl shadow-xl p-10">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">From Vulnerability to Enterprise</h3>
            <p className="text-base leading-relaxed">
              After joining our vocational programme, a participant launched a successful tailoring business, now supporting her family and employing apprentices.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-10">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Healthier Generations</h3>
            <p className="text-base leading-relaxed">
              Regular mobile clinic visits have significantly reduced child malnutrition rates in targeted communities, enabling brighter futures.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-10">
            <h3 className="text-2xl font-semibold text-ehh-orange mb-4">Restored Dignity and Hope</h3>
            <p className="text-base leading-relaxed">
              Counselling and nutrition support have helped numerous households regain stability and actively participate in community development.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Partnership Highlights - Retained as they align with collaborative spirit */}
    <section className="py-24 px-6 bg-ehh-navy/5">
      <div className="container mx-auto max-w-6xl">
        <h2 className="text-3xl md:text-5xl font-bold text-center mb-16">Partnership Highlights: Supporting the Home of Happiness</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src="/assets/images/site-visit.jpeg" 
              alt="Bishop Mbalamweshi, Dr. Ysu Umbalo, and Mrs. Bernadette Mindeo praying over the donated plot in 2019" 
              className="w-full h-64 object-cover" 
            />
            <div className="p-6">
              <p className="text-base leading-relaxed">
                At the construction site, Bishop Mbalamweshi and Dr. Ysu Umbalo accompanied Mrs. Bernadette Mindeo, founder of the Home of Happiness for Children with Disabilities, to visit and pray over the plot donated to the organization. This visit took place in 2019.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src="/assets/images/leveling-ground-wheelchair.jpeg" 
              alt="Contractor leveling the ground to improve wheelchair access at the home" 
              className="w-full h-64 object-cover" 
            />
            <div className="p-6">
              <p className="text-base leading-relaxed">
                To improve access for the many children at the home who use wheelchairs, we engaged a contractor to level the ground as part of our initiatives.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src="/assets/images/children-card-games.jpeg" 
              alt="Children enjoying social time with card games, laughter, and relaxation" 
              className="w-full h-64 object-cover" 
            />
            <div className="p-6">
              <p className="text-base leading-relaxed">
                Some much needed social time with children and cards games to share laughter and relax.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src="/assets/images/children-essentials.jpeg" 
              alt="Children sent off with essential supplies and support" 
              className="w-full h-64 object-cover" 
            />
            <div className="p-6">
              <p className="text-base leading-relaxed">
                Here the children were sent off with some essentials.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <img 
              src="/assets/images/get-together-event.jpeg" 
              alt="Emmanuel Fellowship representatives at a get-together event with friends at the Home of Happiness" 
              className="w-full h-64 object-cover" 
            />
            <div className="p-6">
              <p className="text-base leading-relaxed">
                As a caring partner, Emmanuel was represented by some members at a get together event at the Home of Happiness with several friends.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Future Goals Section - Generalised for consistency */}
    <section className="py-24 px-6 bg-white">
      <div className="container mx-auto max-w-5xl text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-10">Looking Ahead</h2>
        <p className="text-lg md:text-xl leading-relaxed max-w-4xl mx-auto mb-12">
          In the coming years, we aim to expand our reach to thousands of direct beneficiaries and establish self-sustaining Centres of Excellence in multiple provinces—scaling our integrated model for ecosystem-level transformation.
        </p>
        <Link 
          to="/donate" 
          className="inline-block bg-ehh-orange text-ehh-cream px-12 py-5 rounded-lg text-xl font-semibold hover:bg-ehh-coral transition-shadow shadow-xl"
        >
          Partner in Our Vision
        </Link>
      </div>
    </section>
  </div>
);

export default Impact;